/**
 * CallFunnelTile — step-drop-off funnel for Call Mode sessions.
 * Renders in the KPI Command Center. Reads `/v3/call-mode/analytics/funnel`.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PhoneCall, TrendingDown, Loader2 } from "lucide-react";

export default function CallFunnelTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/v3/call-mode/analytics/funnel")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 h-64 grid place-items-center" data-testid="call-funnel-tile">
        <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
      </div>
    );
  }

  const empty = !data || data.total_calls === 0;
  const steps = data?.steps || [];
  const maxReached = Math.max(1, ...steps.map((s) => s.reached));
  const worst = [...steps]
    .filter((s) => s.drop_from_prev_pct != null)
    .sort((a, b) => (b.drop_from_prev_pct || 0) - (a.drop_from_prev_pct || 0))[0];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="call-funnel-tile">
      <div className="flex items-center gap-2 mb-1">
        <span className="h-8 w-8 rounded-lg grid place-items-center bg-indigo-100 text-indigo-700">
          <PhoneCall className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
            Call Mode · Step Drop-off
          </div>
          <div className="text-[15px] font-black text-slate-900">
            {empty ? "No completed calls yet" : `${data.total_calls} completed call${data.total_calls === 1 ? "" : "s"}`}
          </div>
        </div>
      </div>

      {empty ? (
        <div className="text-[12.5px] text-slate-500 mt-2 leading-snug">
          Run a few real seller calls in <b>Call Mode</b> and this tile shows
          which of your 5 steps leaks the most deals. Fix the weak step,
          double your close rate.
        </div>
      ) : (
        <>
          {worst && (worst.drop_from_prev_pct || 0) > 0 && (
            <div className="mt-2 mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
              <TrendingDown className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-[12px] text-amber-900 leading-snug">
                Biggest leak: <b>{worst.label}</b> loses <b>{worst.drop_from_prev_pct}%</b> of calls that made it that far.
              </div>
            </div>
          )}
          <div className="space-y-1.5 mt-3">
            {steps.map((s, i) => {
              const w = Math.max(4, Math.round((s.reached / maxReached) * 100));
              return (
                <div key={s.key} className="text-[11.5px]" data-testid={`funnel-step-${s.key}`}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-slate-700 truncate">
                      <span className="tabular-nums text-slate-400 mr-1.5">{i + 1}.</span>
                      {s.label}
                    </span>
                    <span className="tabular-nums text-slate-500 shrink-0 ml-2">
                      {s.reached} · {s.reach_pct}%
                      {s.drop_from_prev_pct != null && s.drop_from_prev_pct > 0 && (
                        <span className="ml-1.5 text-rose-600 font-bold">−{s.drop_from_prev_pct}%</span>
                      )}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${w}%`,
                        backgroundImage: "linear-gradient(90deg, #6366F1, #4F46E5)",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {data.outcomes && (
            <div className="mt-4 grid grid-cols-4 gap-1.5 text-center">
              {[
                { k: "contract",  label: "Contract" },
                { k: "follow_up", label: "Follow-up" },
                { k: "dead",      label: "Dead" },
                { k: "unknown",   label: "Other" },
              ].map((o) => (
                <div key={o.k} className="rounded-lg bg-slate-50 border border-slate-200 py-1.5">
                  <div className="text-[15px] font-black tabular-nums text-slate-900">{data.outcomes[o.k] || 0}</div>
                  <div className="text-[9.5px] font-bold uppercase tracking-widest text-slate-500">{o.label}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
